import type { ExtractionField, ExtractionStage } from '../../../api/listeners/document-extraction'
import type { RecognizedAddress, RegistrationAddressComponents } from '../../../api/listeners/recognized-address'

const componentNames = ['region', 'district', 'city', 'locality', 'street', 'house', 'building', 'apartment', 'postalCode'] as const
const usable = (field: ExtractionField) => field.status === 'extracted' && !field.candidates.length
    && !['failed', 'invalid', 'conflict'].includes(field.validation.format)
    && !['failed', 'invalid', 'conflict'].includes(field.validation.crossDocument)
const text = (field: ExtractionField) => field.rawValue?.trim() || field.normalizedValue?.trim() || ''
const key = (value: string) => value.normalize('NFKC').toLocaleLowerCase('ru').replace(/ё/g, 'е').replace(/[.,\s]+/g, ' ').trim()

export const registrationAddresses = (stage: ExtractionStage | null): { addresses: RecognizedAddress[]; needsReview: boolean } => {
    const addresses: RecognizedAddress[] = []
    let needsReview = false
    if (!stage || !['succeeded', 'failed'].includes(stage.status)) return { addresses, needsReview }
    for (const part of stage.parts) {
        if (part.status !== 'succeeded' || !part.result) continue
        for (const document of part.result.documents) {
            if (document.type !== 'passport_registration') continue
            const field = document.fields.find(item => item.name === 'registrationAddressAsPrinted')
            if (!field || !text(field)) continue
            const addressConflict = part.result.conflicts.some(conflict =>
                conflict.code === 'registration_ambiguous' || conflict.fields.some(ref => ref.documentId === document.documentId
                    && ['registrationAddressAsPrinted', ...componentNames].includes(ref.field)))
            const addressUnresolved = part.result.unresolved.some(item => item.documentId === document.documentId
                && (item.code === 'registration_ambiguous' || (item.field !== null && ['registrationAddressAsPrinted', ...componentNames].includes(item.field))))
            const deregistered = document.fields.some(item => item.name === 'deregistrationDate' && usable(item) && text(item))
            if (!usable(field) || addressConflict || addressUnresolved || deregistered || document.quality.some(item => item.issue === 'multiple_stamps')) {
                needsReview = true
                continue
            }
            const components: RegistrationAddressComponents = {}
            let componentConflict = false
            for (const name of componentNames) {
                const component = document.fields.find(item => item.name === name)
                if (!component || component.status === 'not_present' || component.status === 'not_applicable') continue
                if (!usable(component)) { componentConflict = true; break }
                if (text(component)) components[name] = text(component)
            }
            if (componentConflict) { needsReview = true; continue }
            const address = { query: text(field), components }
            if (!addresses.some(existing => key(existing.query) === key(address.query) && JSON.stringify(existing.components) === JSON.stringify(components))) addresses.push(address)
        }
    }
    return { addresses, needsReview }
}
